/**
 * ZOUR-67 — Agent Review API client.
 *
 * Thin on purpose. The backend already decides the four statuses and whether a
 * bill is Ready; re-deriving either here would give the page a second opinion
 * that could disagree with the cron's.
 */

import { getStoredToken } from "../../../../services/api";
import { API_BASE_URL } from "../../../../config/api";
import {
  AgentReviewDashboard,
  AgentReviewFilters,
  AgentReviewFinding,
  AgentReviewListResponse,
  AgentReviewRunDetail,
  FindingDisposition,
} from "../../domain/entities/AgentReviewEntities";

export class AgentReviewApiError extends Error {
  /** HTTP status, or 0 when the request never got an answer at all. */
  public status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "AgentReviewApiError";
    this.status = status;
  }

  /**
   * True when the server never gave a verdict — the request died in transit,
   * or a gateway gave up on it.
   *
   * This is the difference between "it might still be happening" and "it
   * definitely did not happen", and Re-check depends on it: the backend sets
   * `ignore_user_abort(true)`, so a dropped connection really can mean the run
   * is still going, while a 403 means it never started.
   */
  public get isIndefinite(): boolean {
    return this.status === 0 || [502, 503, 504].includes(this.status);
  }
}

const authHeaders = (): Record<string, string> => {
  const token = getStoredToken();
  if (!token) {
    throw new AgentReviewApiError("No authentication token found", 401);
  }
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/agent-review${path}`, {
      ...init,
      headers: authHeaders(),
    });
  } catch (err) {
    // The request never reached a verdict. Status 0 is what tells the caller
    // that, so it must not be collapsed into a generic Error.
    if (err instanceof AgentReviewApiError) {
      throw err;
    }
    throw new AgentReviewApiError(
      err instanceof Error ? err.message : "The request could not be sent",
      0,
    );
  }

  const body = await response.json().catch(() => null);

  if (!response.ok || !body?.success) {
    throw new AgentReviewApiError(
      body?.message || `Request failed: ${response.statusText}`,
      response.status,
    );
  }

  return body.data as T;
}

/**
 * The list.
 *
 * A tenant whose `agent_review_*` migrations have not been run yet gets a 200
 * with `available: false` — not an error, because nothing is wrong; the feature
 * simply is not switched on there yet.
 */
export const fetchAgentReviewList = async (
  filters: AgentReviewFilters = {},
): Promise<AgentReviewListResponse> => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  });

  const query = params.toString();
  return request<AgentReviewListResponse>(query ? `?${query}` : "");
};

/** One run, with its findings, its steps and the data it looked at. */
export const fetchAgentReviewRun = (runId: number) =>
  request<AgentReviewRunDetail>(`/runs/${runId}`);

/** The latest run for a bill. Returns `run: null` when it has never been reviewed. */
export const fetchAgentReviewForBill = (billId: number) =>
  request<AgentReviewRunDetail>(`/bills/${billId}`);

/**
 * Ask for a fresh review now.
 *
 * This can take minutes — the backend sets `ignore_user_abort(true)` so the run
 * completes and is recorded even if this request is cut off. The page should
 * therefore treat a network failure here as "probably still running" and poll
 * the bill, not as "it did not happen".
 */
export const recheckBill = (billId: number) =>
  request<{
    run_id: number | null;
    status: string | null;
    verdict: string | null;
    is_ready: number | null;
    summary: string | null;
  }>(`/bills/${billId}/recheck`, { method: "POST" });

/** "Not an error" / "Confirmed" / clear. Needs `bill.edit`. */
export const setFindingDisposition = (
  findingId: number,
  disposition: FindingDisposition,
) =>
  request<AgentReviewFinding>(`/findings/${findingId}/disposition`, {
    method: "POST",
    body: JSON.stringify({ disposition }),
  });

export const fetchAgentReviewDashboard = (days = 30, includePosted = false) =>
  request<AgentReviewDashboard>(
    `/dashboard?days=${days}${includePosted ? "&include_posted=1" : ""}`,
  );
